import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { api } from "../api";
import type { Me } from "../ui";

export type { Me };
export type SiteArea = { id: string; kind: "page" | "portal" | "staff" | "module"; label: string; path: string | null; allowed: boolean; enabled: boolean; available?: boolean; requiresLogin: boolean; canManage: boolean };
// `loaded` turns true once /api/v1/site has answered (or failed), so gated pages can wait for it.
export type SiteState = { me: Me; areas: SiteArea[] | null; features: { tickets: boolean }; loaded: boolean };

const SiteContext = createContext<SiteState>({ me: { user: null, permissions: [] }, areas: null, features: { tickets: false }, loaded: true });

export function SiteProvider({ me, children }: { me: Me; children: ReactNode }) {
  const [state, setState] = useState<{ areas: SiteArea[] | null; features: { tickets: boolean }; loaded: boolean }>({ areas: null, features: { tickets: false }, loaded: false });
  useEffect(() => { let active = true; api<{ areas: SiteArea[]; features?: { tickets: boolean } }>("/api/v1/site").then((data) => active && setState({ areas: data.areas, features: { tickets: Boolean(data.features?.tickets) }, loaded: true })).catch(() => active && setState({ areas: null, features: { tickets: false }, loaded: true })); return () => { active = false; }; }, [me.user?.id]);
  return <SiteContext.Provider value={{ me, ...state }}>{children}</SiteContext.Provider>;
}

export function useSite() { return useContext(SiteContext); }

// When access data is not loaded yet (or failed), assume the default: visible.
// The server enforces every rule regardless of what the browser shows.
export function useArea(id: string): SiteArea | null {
  const { areas } = useSite();
  return areas?.find((area) => area.id === id) || null;
}

export function areaVisible(area: SiteArea | null, signedIn: boolean) {
  if (!area) return true;
  return area.allowed || (area.requiresLogin && !signedIn);
}
